import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tabs from './Tabs';
import Header from './Header';
import MetricCards from './MetricCards';
import NewInvoicePanel from './NewInvoicePanel';
import AgingTabContent from './AgingTabContent';
import AppLayout from '../../components/AppLayout';
import InvoicesTabContent from './InvoicesTabContent';
import InvoicesTabContent2 from './InvoicesTabContent2';
import InvoiceDetailPanel from './InvoiceDetailPanel';
import useInvoicingFunctions from './useInvoicingFunctions';

// D-27: "outstanding" is a virtual bucket = anything that isn't paid or
// cancelled (draft + sent + overdue). Backend stores only concrete
// statuses; the useInvoicingFunctions filter expands 'outstanding' to
// the concrete set at query time.
const STATUS_OPTIONS = ['all', 'outstanding', 'draft', 'sent', 'paid', 'overdue', 'cancelled'];
const STATUS_LABEL_KEYS = { all: 'screens.invoicing.statusAll', outstanding: 'screens.invoicing.statusOutstanding', draft: 'screens.invoicing.statusDraft', sent: 'screens.invoicing.statusSent', paid: 'screens.invoicing.statusPaid', overdue: 'screens.invoicing.statusOverdue', cancelled: 'screens.invoicing.statusCancelled' };

// ─── Invoicing Screen ─────────────────────────────────────
const InvoicingScreen = () => {
  const { t } = useTranslation();
  const STATUS_LABELS = Object.fromEntries(Object.entries(STATUS_LABEL_KEYS).map(([k, v]) => [k, t(v)]));
  // D-28: track which invoice row is expanded into the detail panel.
  // Q-08 print action lives inside InvoiceDetailPanel.
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const {
    invoices,
    aging,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    activeTab,
    setActiveTab,
    page,
    setPage,
    paying,
    showNew,
    setShowNew,
    loading,
    handleCloseNew,
    handleCreated,
    handleRecordPayment,
    handleMarkAsSent,
    filtered,
    paginated,
    totalPages,
    totalOutstanding,
    totalOverdue,
    totalPaid,
    TABS,
  } = useInvoicingFunctions();

  if (loading) {
    return (
      <AppLayout>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
          <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {showNew && <NewInvoicePanel onClose={handleCloseNew} onCreated={handleCreated} />}
      {selectedInvoiceId && (
        <InvoiceDetailPanel invoiceId={selectedInvoiceId} onClose={() => setSelectedInvoiceId(null)} />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Header setShowNew={setShowNew} invoices={invoices} />

        {/* Metric cards */}
        <MetricCards totalOutstanding={totalOutstanding} totalOverdue={totalOverdue} totalPaid={totalPaid} />

        {/* Tabs */}
        <Tabs TABS={TABS} activeTab={activeTab} setActiveTab={setActiveTab} />

        {activeTab === 'invoices' && (
          <InvoicesTabContent
            search={search}
            setSearch={setSearch}
            setPage={setPage}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            STATUS_OPTIONS={STATUS_OPTIONS}
            STATUS_LABELS={STATUS_LABELS}
            filtered={filtered}
            paginated={paginated}
            handleMarkAsSent={handleMarkAsSent}
            handleRecordPayment={handleRecordPayment}
            paying={paying}
            onRowClick={setSelectedInvoiceId}
          />
        )}

        {activeTab === 'aging' && <AgingTabContent aging={aging} invoices={invoices} handleRecordPayment={handleRecordPayment} paying={paying} />}

        {activeTab === 'invoices' && <InvoicesTabContent2 paginated={paginated} filtered={filtered} totalPages={totalPages} page={page} setPage={setPage} />}
      </div>
    </AppLayout>
  );
};

export default InvoicingScreen;
